import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingBuffer from '../components/LoadingBuffer';
import { handleUniversalAddToCart, getProductStock } from '../utils/cartHelper';
import ProductCartButton from '../components/ProductCartButton';

const normalizeCategory = (val = '') =>
  String(val || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const resolveProductCategory = (rawCat = '', apiCategories = []) => {
  const trimmed = String(rawCat || '').trim();
  if (!trimmed) return '';
  const norm = normalizeCategory(trimmed);

  try {
    const renameMapRaw = localStorage.getItem('vanya_category_rename_map');
    if (renameMapRaw) {
      const renameMap = JSON.parse(renameMapRaw);
      if (renameMap && renameMap[norm]) {
        const mapped = String(renameMap[norm]).trim();
        const matchedMapped = (apiCategories || []).find(
          (c) => normalizeCategory(c?.category) === normalizeCategory(mapped)
        );
        return matchedMapped ? matchedMapped.category : mapped;
      }
    }
  } catch (e) {}

  const matched = (apiCategories || []).find(
    (c) => normalizeCategory(c?.category) === norm
  );
  return matched?.category || trimmed;
};

const getProductImageFallback = (item) => {
  if (item?.img_url && typeof item.img_url === 'string' && item.img_url.trim() !== '') {
    return item.img_url;
  }
  if (Array.isArray(item?.thumbnails) && item.thumbnails[0]) {
    return item.thumbnails[0];
  }
  if (Array.isArray(item?.variants)) {
    for (const v of item.variants) {
      if (v?.mainImage) return v.mainImage;
      if (v?.main_image) return v.main_image;
      if (Array.isArray(v?.thumbnails) && v.thumbnails[0]) return v.thumbnails[0];
    }
  }
  return '';
};

const CollectionsPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeSubCategory, setActiveSubCategory] = useState('All');
  const [products, setProducts] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  useEffect(() => {
    const fetchProductsAndCategories = async () => {
      try {
        const [prodRes, catRes] = await Promise.all([
          fetch('https://api.vanyacollectionsrajahmundry.com/products/all'),
          fetch('https://api.vanyacollectionsrajahmundry.com/categories').catch(() => null),
        ]);
        const data = await prodRes.json();
        const catData = catRes && catRes.ok ? await catRes.json() : [];
        const validApiCategories = Array.isArray(catData) ? catData : [];
        setApiCategories(validApiCategories);

        const activeData = (Array.isArray(data) ? data : []).filter(item => !item.is_hold);

        const mappedProducts = activeData.map(item => {
          const resolvedCat = resolveProductCategory(item.category, validApiCategories);
          return {
            ...item,
            id: item.id,
            name: item.name,
            category: resolvedCat,
            subCategory: item.sub_category || item.subCategory || '',
            price: `₹${Number(item.price).toLocaleString()}`,
            originalPrice: `₹${Number(item.old_price).toLocaleString()}`,
            discount: `-${item.discount}%`,
            rating: 4 + Math.random(),
            reviews: Math.floor(Math.random() * 100) + 10,
            tag: Number(item.discount) > 20 ? 'BESTSELLER' : 'NEW',
            image: getProductImageFallback(item),
            variants: Array.isArray(item.variants) ? item.variants : [],
            stock: item.stock !== undefined ? Number(item.stock) : 0,
          };
        });

        setProducts(mappedProducts);

        // Build canonical category list from /categories first, then any extra product categories
        const categoryNames = [];
        const seenKeys = new Set();

        validApiCategories.forEach(c => {
          const name = String(c?.category || '').trim();
          const key = normalizeCategory(name);
          if (name && key && !seenKeys.has(key)) {
            seenKeys.add(key);
            categoryNames.push(name);
          }
        });

        mappedProducts.forEach(p => {
          const name = String(p?.category || '').trim();
          const key = normalizeCategory(name);
          if (name && key && !seenKeys.has(key)) {
            seenKeys.add(key);
            categoryNames.push(name);
          }
        });

        setCategories(['All', ...categoryNames]);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsAndCategories();

    const handleSync = () => fetchProductsAndCategories();
    window.addEventListener('focus', handleSync);
    window.addEventListener('storage', handleSync);
    window.addEventListener('vanya_category_sync', handleSync);
    return () => {
      window.removeEventListener('focus', handleSync);
      window.removeEventListener('storage', handleSync);
      window.removeEventListener('vanya_category_sync', handleSync);
    };
  }, []);

  // Update subcategories when a category is selected
  useEffect(() => {
    if (activeCategory === 'All') {
      setSubCategories([]);
      setActiveSubCategory('All');
    } else {
      const normActive = normalizeCategory(activeCategory);
      const subs = products
        .filter(p => normalizeCategory(p.category) === normActive)
        .map(p => p.subCategory)
        .filter(Boolean);
      setSubCategories(['All', ...[...new Set(subs)]]);
      setActiveSubCategory('All');
    }
  }, [activeCategory, products]);

  // Filter products by category & subcategory
  const filteredProducts = products.filter(p =>
    (activeCategory === 'All' || normalizeCategory(p.category) === normalizeCategory(activeCategory)) &&
    (activeSubCategory === 'All' || p.subCategory === activeSubCategory)
  );

  // Collections summary
  const handleAddToCart = async (prod) => {
    const stock = getProductStock(prod);
    if (stock <= 0) {
      alert(`"${prod.name}" is currently out of stock.`);
      return;
    }
    try {
      const res = await handleUniversalAddToCart(prod);
      if (res.outOfStock) {
        alert(res.message || `"${prod.name}" is currently out of stock.`);
        return;
      }
      if (res.needsColourSelection) {
        alert("Please select a colour first.");
        navigate(`/product/${prod.id}`);
        return;
      }
      if (res.success) {
        alert(res.message || `${prod.name} added to cart!`);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to add to cart");
    }
  };

  const collectionsSummary = categories
    .filter(cat => cat !== 'All')
    .map((cat, idx) => {
      const normCat = normalizeCategory(cat);
      const matchedApiCat = apiCategories.find(c => normalizeCategory(c?.category) === normCat);
      const matchingProducts = products.filter(p => normalizeCategory(p.category) === normCat);
      const firstProduct = matchingProducts.find(p => p.image) || matchingProducts[0];
      return {
        id: matchedApiCat?.id || idx + 1,
        category: cat,
        title: cat,
        count: `${matchingProducts.length} DESIGNS`,
        description: `Explore our ${cat.toLowerCase()} collection.`,
        image: matchedApiCat?.image_url || (firstProduct ? firstProduct.image : 'https://via.placeholder.com/400'),
        btnText: `Explore ${cat}`
      };
    });

  return (
    <div className="vanya-wrapper">
      <style>{`
        :root { 
          --primary-purple: #633878; 
          --accent-gold: #fbd349; 
          --purple-hover: #74418c;
          --bg-light: #f8f4fa; 
          --text-gray: #666; 
          --border: #e6d8ed;
        }

        * { box-sizing: border-box; }
        .vanya-wrapper { font-family: 'Playfair Display', serif; color: #2c2c2c; background-color: var(--bg-light); min-height: 100vh; }
        
        header { 
          background: var(--primary-purple); 
          color: var(--accent-gold); 
          text-align: center; 
          padding: 60px 20px; 
          background-image: radial-gradient(circle, #74418c 1px, transparent 1px);
          background-size: 20px 20px;
          box-shadow: inset 0 -10px 20px rgba(0,0,0,0.15);
        }
        
        .subtitle { font-size: 0.8rem; letter-spacing: 2.5px; font-weight: 600; color: var(--accent-gold); margin-bottom: 5px; }
        .main-title { font-size: 2.8rem; color: var(--accent-gold); margin: 10px 0; font-weight: 700; letter-spacing: 1px; }        .filter-tabs { 
          display: flex; 
          justify-content: center; 
          gap: 10px; 
          padding: 15px 20px; 
          background: white; 
          position: sticky; 
          top: 0; 
          z-index: 100; 
          border-bottom: 1px solid var(--border); 
          box-shadow: 0 4px 18px rgba(99, 56, 120, 0.06);
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .filter-tabs::-webkit-scrollbar { display: none; }
        
        .tab-btn { 
          padding: 9px 22px; 
          border-radius: 999px; 
          border: 1px solid rgba(99, 56, 120, 0.18); 
          background: #fdfafd; 
          color: var(--primary-purple);
          cursor: pointer; 
          transition: all 0.25s ease; 
          font-weight: 600;
          font-size: 0.85rem;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .tab-btn:hover { 
          border-color: var(--primary-purple);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(99, 56, 120, 0.1);
        }
        .tab-btn.active { 
          background: linear-gradient(135deg, #4d1d57 0%, #2f1035 100%);
          color: #fbeea4; 
          border-color: #d4af37;
          box-shadow: 0 4px 14px rgba(60, 20, 75, 0.25);
        }

        .summary-list { max-width: 1100px; margin: 40px auto; padding: 0 20px; }
        
        .summary-card { 
          display: flex; 
          align-items: center; 
          background: white; 
          border-radius: 16px; 
          overflow: hidden; 
          margin-bottom: 40px; 
          border: 1px solid var(--border);
          box-shadow: 0 8px 24px rgba(99, 56, 120, 0.08);
          transition: transform 0.3s, box-shadow 0.3s;
        }
        .summary-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 36px rgba(99, 56, 120, 0.15);
        }
        .summary-card.reverse { flex-direction: row-reverse; }
        .card-img { flex: 1.2; height: 400px; background: #f5eef7; }
        .card-img img { width: 100%; height: 100%; object-fit: cover; }
        .card-info { flex: 1; padding: 40px; }
        
        .explore-btn { 
          background: linear-gradient(135deg, #4d1d57 0%, #2f1035 100%); 
          color: #fbeea4; 
          border: 1px solid rgba(212, 175, 55, 0.3); 
          padding: 12px 24px; 
          border-radius: 8px; 
          cursor: pointer; 
          display: flex; 
          align-items: center; 
          gap: 10px; 
          font-weight: 700;
          font-size: 0.9rem;
          transition: transform 0.2s, box-shadow 0.2s;
          box-shadow: 0 4px 14px rgba(60, 20, 75, 0.2);
        }
        .explore-btn:hover { 
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(60, 20, 75, 0.3);
        }

        .product-grid { 
          display: grid; 
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); 
          gap: 24px; 
          max-width: 1200px; 
          margin: 35px auto; 
          padding: 20px; 
          background: transparent;
        }
        
        .product-card { 
          background: white; 
          border-radius: 14px; 
          overflow: hidden; 
          position: relative; 
          border: 1px solid var(--border);
          box-shadow: 0 8px 24px rgba(99, 56, 120, 0.06);
          transition: transform 0.3s, box-shadow 0.3s;
        }
        .product-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 16px 36px rgba(99, 56, 120, 0.14);
          border-color: #d1b8dc;
        }

        .badge { 
          position: absolute; 
          top: 12px; 
          left: 12px; 
          background: #3d1445; 
          color: #fbeea4; 
          font-size: 0.7rem; 
          font-weight: 700; 
          padding: 4px 10px; 
          border-radius: 999px; 
          z-index: 2; 
          letter-spacing: 0.8px;
          border: 1px solid rgba(226, 188, 83, 0.4);
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        
        .discount-tag { 
          position: absolute; 
          top: 42px; 
          left: 12px; 
          background: linear-gradient(135deg, #b31e28 0%, #85121a 100%); 
          color: white; 
          padding: 3px 9px; 
          border-radius: 999px; 
          font-size: 0.7rem; 
          font-weight: 700;
          z-index: 2;
          box-shadow: 0 2px 8px rgba(179, 30, 40, 0.3);
        }
        
        .wishlist-icon { 
          position: absolute; 
          top: 12px; 
          right: 12px; 
          background: rgba(255, 255, 255, 0.9); 
          backdrop-filter: blur(4px);
          border-radius: 50%; 
          width: 36px; 
          height: 36px; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          cursor: pointer; 
          box-shadow: 0 4px 12px rgba(0,0,0,0.12);
          color: var(--primary-purple);
          z-index: 2;
          transition: transform 0.2s, background 0.2s;
        }
        .wishlist-icon:hover {
          transform: scale(1.1);
          background: #ffffff;
        }
        
        .prod-img { height: 360px; position: relative; background: #f5eef7; overflow: hidden; }
        .prod-img img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s cubic-bezier(0.165, 0.84, 0.44, 1); }
        .product-card:hover .prod-img img { transform: scale(1.05); }

        .prod-details { padding: 18px 20px 20px; background: #ffffff; }
        .price-row { display: flex; align-items: center; gap: 10px; margin-top: 8px; }
        .current-price { font-weight: 700; font-size: 1.2rem; color: #3d1445; }
        .old-price { text-decoration: line-through; color: #a48da7; font-size: 0.85rem; }
        
        .btn-group { display: flex; gap: 8px; margin-top: 15px; }
        .quick-view { 
          flex: 1; 
          padding: 10px 12px; 
          border: 1px solid rgba(99, 56, 120, 0.25); 
          background: #fcf8fd; 
          color: var(--primary-purple); 
          border-radius: 8px; 
          cursor: pointer; 
          font-weight: 700; 
          font-size: 0.8rem; 
          transition: all 0.2s; 
        }
        .quick-view:hover { 
          background: #f4e8f7; 
          border-color: var(--primary-purple);
        }
        
        .add-cart { 
          flex: 1.5; 
          padding: 10px 12px; 
          background: linear-gradient(135deg, #e2bc53 0%, #c99b27 100%); 
          color: #2a1236; 
          border: none; 
          border-radius: 8px; 
          cursor: pointer; 
          font-weight: 700; 
          font-size: 0.85rem; 
          letter-spacing: 0.3px; 
          transition: transform 0.2s, box-shadow 0.2s; 
          box-shadow: 0 4px 12px rgba(201, 155, 39, 0.3);
        }
        .add-cart:hover { 
          transform: translateY(-2px);
          box-shadow: 0 6px 18px rgba(201, 155, 39, 0.45);
        }

        /* Subcategory Tabs */
        .sub-filter-tabs { 
          display: flex; 
          justify-content: center; 
          gap: 8px; 
          padding: 12px 20px; 
          background: #f8f2fa; 
          border-bottom: 1px solid var(--border); 
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .sub-filter-tabs::-webkit-scrollbar { display: none; }
        .sub-tab-btn { 
          padding: 6px 16px; 
          border-radius: 999px; 
          border: 1px solid rgba(99, 56, 120, 0.15); 
          background: white; 
          cursor: pointer; 
          transition: all 0.2s ease; 
          font-size: 0.8rem; 
          color: var(--primary-purple); 
          font-weight: 600; 
          white-space: nowrap;
          flex-shrink: 0;
        }
        .sub-tab-btn:hover {
          border-color: var(--primary-purple);
        }
        .sub-tab-btn.active { 
          background: linear-gradient(135deg, #e2bc53, #c99b27); 
          color: #2a1236; 
          border-color: #d4af37; 
          box-shadow: 0 2px 8px rgba(201, 155, 39, 0.3);
        }

        @media (max-width: 768px) { 
          .summary-card, .summary-card.reverse { flex-direction: column; } 
          .card-img { height: 250px; width: 100%; } 
          .product-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; padding: 12px; } 
          .prod-img { height: 200px; } 
          .main-title { font-size: 2rem; }
          .filter-tabs { justify-content: flex-start; padding: 12px 14px; }
        }
      `}</style>

      <header>
        <p className="subtitle">CURATED FOR YOU</p>
        <h1 className="main-title">{activeCategory === 'All' ? 'All Collections' : activeCategory}</h1>
      </header>

      {/* Category Filter */}
      <nav className="filter-tabs">
        {categories.map(cat => (
          <button
            key={cat}
            className={`tab-btn ${activeCategory === cat ? 'active' : ''}`}
            onClick={() => setActiveCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </nav>

      {/* Subcategory Filter */}
      {subCategories.length > 1 && (
        <nav className="sub-filter-tabs">
          {subCategories.map(sub => (
            <button
              key={sub}
              className={`sub-tab-btn ${activeSubCategory === sub ? 'active' : ''}`}
              onClick={() => setActiveSubCategory(sub)}
            >
              {sub}
            </button>
          ))}
        </nav>
      )}

      <main>
        {loading ? (
          <LoadingBuffer 
            message="Curating Royal Handloom Collections..." 
            minHeight="450px" 
          />
        ) : activeCategory === 'All' ? (
          <div className="summary-list">
            {collectionsSummary.map((item, index) => (
              <article key={item.id} className={`summary-card ${index % 2 !== 0 ? 'reverse' : ''}`}>
                <div className="card-img"><img src={item.image} alt={item.title} /></div>
                <div className="card-info">
                  <span style={{color: 'var(--primary-purple)', fontWeight: 'bold', fontSize: '0.75rem', letterSpacing: '1.5px', textTransform: 'uppercase'}}>{item.count}</span>
                  <h2 style={{fontSize: '2.2rem', margin: '10px 0', color: '#2c2c2c'}}>{item.title}</h2>
                  <p style={{color: 'var(--text-gray)', marginBottom: '20px'}}>{item.description}</p>
                  <button className="explore-btn" onClick={() => setActiveCategory(item.category)}>
                    {item.btnText} <span>→</span>
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="product-grid">
            {filteredProducts.map(prod => {
              const isOutOfStock = getProductStock(prod) <= 0;
              return (
                <div key={prod.id} className="product-card">
                  {isOutOfStock ? (
                    <span className="badge" style={{ background: '#b91c1c', border: '1px solid #ef4444', color: '#fff' }}>
                      OUT OF STOCK
                    </span>
                  ) : (
                    <span className="badge">{prod.tag}</span>
                  )}
                  <span className="discount-tag">{prod.discount}</span>
                  <div className="wishlist-icon">♡</div>
                  <div className="prod-img"><img src={prod.image} alt={prod.name} /></div>
                  <div className="prod-details">
                    <p style={{fontSize: '0.75rem', color: '#8a7a94', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '600'}}>{prod.category}</p>
                    <h3 style={{fontSize: '1.1rem', margin: '8px 0', color: '#2c2c2c', fontWeight: '600'}}>{prod.name}</h3>
                    <div style={{color: '#e6af2e', fontSize: '0.85rem', margin: '6px 0'}}>
                      {'★'.repeat(Math.floor(prod.rating))} <span style={{color: '#888', fontSize: '0.8rem'}}>({prod.reviews})</span>
                    </div>
                    <div className="price-row">
                      <span className="current-price">{prod.price}</span>
                      <span className="old-price">{prod.originalPrice}</span>
                    </div>
                    <div className="btn-group">
                      <button className="quick-view" onClick={() => navigate(`/product/${prod.id}`)}>👁 Quick View</button>
                      <ProductCartButton product={prod} buttonText="🛒 Add to Cart" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default CollectionsPage;